import React, { Component } from 'react';

import classnames from 'classnames';
import { Bell } from 'lucide-react';
import { browserHistory } from 'react-router';
import { compose } from 'redux';

import { reloadNotifications } from 'common/actions/notifications';
import AJAX from 'common/AJAX';
import Portal from 'common/common/Portal';
import Tooltip from 'common/common/Tooltip';
import { TintColorContext } from 'common/containers/TintColorContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import connect from 'common/core/connect';
import Link from 'common/Link';
import Spinner from 'common/Spinner';
import Tappable from 'common/Tappable';
import IconButtonV2 from 'common/ui/IconButtonV2';
import withContexts from 'common/util/withContexts';

import Notification from './Notification';

import type { Viewer } from 'common/api/endpoints/viewer';
import type { Board } from 'common/api/resources/board';
import type { NotificationsState } from 'common/reducers/notifications';

import 'css/components/_NotificationsMenu.scss';

interface BaseProps {
  board?: Board;
  button?: React.ComponentType<{ onClick: () => void }>;
  iconStyle?: 'light' | 'dark';
  linkToAdmin?: boolean;
  position?: 'bottom' | 'right';
  notifications: NotificationsState;
  showTooltip?: boolean;
  tint?: boolean;
  tintColor?: string;
  viewer?: Viewer;
  v2?: boolean;
}

interface Props extends BaseProps {
  reloadNotifications: () => void;
}

class NotificationsMenu extends Component<Props> {
  static defaultProps = {
    iconStyle: 'dark',
    linkToAdmin: false,
    showTooltip: true,
    position: 'bottom',
    tint: false,
  };

  state = {
    menuOpen: false,
  };

  private containerRef = React.createRef<HTMLDivElement>();
  private _lastFetchSeconds: number | null = null;
  private _unlisten: (() => void) | null = null;
  private _markingAllSeen = false;
  private _markingAllRead = false;

  reloadNotifications = () => {
    this.props.reloadNotifications();
    this._lastFetchSeconds = new Date().getSeconds();
  };

  // handles reloading notifications when the user navigates to a new page
  // we limit the number of requests to 1 per 30 seconds regardless of how many times the user navigates
  onRouteChange = () => {
    this.setState({
      menuOpen: false,
    });

    const now = new Date();

    // default to 0 to handle this being null on the first render
    const lastFetch = this._lastFetchSeconds ?? 0;
    const secondsSinceLastFetch = now.getSeconds() - lastFetch;

    // if less than 30 seconds have passed, don't refetch notifications
    if (secondsSinceLastFetch < 30) {
      return;
    }

    this.reloadNotifications();
  };

  componentDidMount() {
    this._lastFetchSeconds = new Date().getSeconds();
    this._unlisten = browserHistory.listen(this.onRouteChange);
  }

  componentWillUnmount() {
    if (this._unlisten) {
      this._unlisten();
    }
  }

  onDropdownBlur = () => {
    const { menuOpen } = this.state;
    if (!menuOpen) {
      return;
    }
    this.setState({ menuOpen: false });
  };

  onToggleNotifications = () => {
    this.setState({
      menuOpen: !this.state.menuOpen,
    });
    this.markAllAsSeen();
  };

  markAllAsSeen = async () => {
    const { board, notifications } = this.props;
    if (!notifications || notifications.unreadCount === 0 || this._markingAllSeen) {
      return;
    }

    this._markingAllSeen = true;
    await AJAX.post('/api/notifications/markAllSeen', {
      ...(board && { boardID: board._id }),
    });
    this._markingAllSeen = false;
    this.reloadNotifications();
  };

  markAllAsRead = async () => {
    const { board } = this.props;
    if (this._markingAllRead) {
      return;
    }

    this._markingAllRead = true;
    await AJAX.post('/api/notifications/markAllRead', {
      ...(board && { boardID: board._id }),
    });
    this._markingAllRead = false;
    this.reloadNotifications();
  };

  shouldShowTooltip = () => {
    return this.props.showTooltip && !this.state.menuOpen;
  };

  renderButton() {
    const { notifications, button: Button } = this.props;

    const unreadCount = notifications?.unreadCount ?? 0;

    if (this.props.v2) {
      return (
        <div className="notificationsV2Button">
          <IconButtonV2
            onClick={this.onToggleNotifications}
            size="medium"
            icon={Bell}
            variant="outlined"
            aria-label="Notifications"
          />
          {unreadCount > 0 ? <span className="badge">{Math.min(unreadCount, 99)}</span> : null}
        </div>
      );
    } else if (Button) {
      return (
        <div className="notificationsV2Button">
          <Button onClick={this.onToggleNotifications} />
          {unreadCount > 0 ? <span className="badge">{Math.min(unreadCount, 99)}</span> : null}
        </div>
      );
    }

    return <Tappable onTap={this.onToggleNotifications}>{this.renderNotificationMenu()}</Tappable>;
  }

  renderNotificationsLink() {
    const { linkToAdmin } = this.props;
    if (linkToAdmin) {
      return '/admin/notifications';
    }

    return '/notifications';
  }

  renderNotificationMenu() {
    const { iconStyle, notifications } = this.props;

    const unreadCount = notifications?.unreadCount ?? 0;

    return (
      <div className={classnames('menu menuIconButton', { hasUnread: unreadCount > 0 })}>
        <div className="navIconContainer">
          <Bell
            className={classnames('notificationsIcon', { light: iconStyle === 'light' })}
            size="20"
          />
        </div>
        {unreadCount ? (
          <span
            className="badge"
            style={this.props.tint ? { background: this.props.tintColor } : {}}>
            {Math.min(unreadCount, 99)}
          </span>
        ) : null}
      </div>
    );
  }

  renderNotifications() {
    const { notifications } = this.props;

    if (!notifications || notifications.loading) {
      return (
        <div className="notificationsDropdownContainer">
          <div className="notificationsDropdown">
            <div className="spinnerContainer">
              <Spinner />
            </div>
          </div>
        </div>
      );
    }

    if (!notifications.items.length) {
      return (
        <div className="notificationsDropdownContainer">
          <div className="notificationsDropdown">
            <div className="nullState">
              <span>You have no notifications.</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="notificationsDropdownContainer">
        <div className="notificationsDropdown">
          <div className="notificationsDropdownHeader">
            <h3>Notifications</h3>
            <Tappable onTap={this.markAllAsRead}>
              <span style={this.props.tint ? { color: this.props.tintColor } : {}}>
                Mark all as read
              </span>
            </Tappable>
          </div>
          <div className="notifications">
            {notifications.items.map((notification) => (
              <Notification key={notification._id} notification={notification} />
            ))}
          </div>
          <div className="notificationsDropdownFooter">
            <Link to={this.renderNotificationsLink()}>See all</Link>
          </div>
        </div>
      </div>
    );
  }

  renderPortal() {
    if (!this.state.menuOpen) {
      return null;
    }

    return (
      <Portal
        align="end"
        className={classnames('dropdownPortal', 'notificationsMenuPortal')}
        onBlur={this.onDropdownBlur}
        position={this.props.position}
        relativeToRef={this.containerRef}>
        {this.renderNotifications()}
      </Portal>
    );
  }

  render() {
    const { viewer } = this.props;
    if (!viewer || viewer.loggedOut) {
      return null;
    }

    return (
      <div className="notificationsMenu" ref={this.containerRef}>
        <Tooltip
          className="adminNavTooltipIcon"
          disabled={!this.shouldShowTooltip()}
          position={this.props.position}
          value="Notifications">
          {this.renderButton()}
        </Tooltip>
        {this.renderPortal()}
      </div>
    );
  }
}

export default compose(
  connect(null, { reloadNotifications }),
  withContexts(
    {
      tintColor: TintColorContext,
      viewer: ViewerContext,
    },
    {
      forwardRef: true,
    },
  ),
)(NotificationsMenu) as unknown as React.ComponentClass<BaseProps>;
